<script setup lang="ts">
import { useApiFetch } from "~/composible/useApiFetch";
import { useDevice } from "#imports";

const router = useRouter();
const route = useRoute();
const config = useRuntimeConfig();
const { isMobile } = useDevice();

const currentPage = ref(route.query.page ? parseInt(route.query.page as string) : 1);

const { data, pending } = await useApiFetch('/reviews', {
  query: { page: currentPage },
  watch: [currentPage],
});

const onChangePage = (page: number) => {
  router.push({ query: { page: page } });
  if (process.client){
    nextTick(() => window.scrollTo({ top: 0 }))
  }
};

const { data: popularBooks, pending: pendingPopular } = await useApiFetch(`/novels/_/similar`, {
  transform: (popularBooks) => popularBooks.data,
  lazy: true,
});

const headPaginationLinks = computed(() => {
  let links = [];
  if (data.value?.meta.last_page > 1 && currentPage.value > 1) {
    links.push({rel: 'prev', href: config.public.siteURL + route.path + '?page=' + (currentPage.value - 1)})
  }
  if (currentPage.value < data.value.meta.last_page) {
    links.push({rel: 'next', href: config.public.siteURL + route.path + '?page=' + (currentPage.value + 1)})
  }

  return links;
});

useHead({
  link: headPaginationLinks
});

useSeoMeta({
  title: 'Novels, Books and Stories reviews, Rankings and more by FictionMe' + (currentPage.value > 1 ? ` - Page ${currentPage.value}` : ``),
  description: 'Dive into FictionMe for captivating novel reviews, up-to-date rankings, and essential writing resources. Discover your next favorite story today!',
  ogTitle: 'Novels, Books and Stories reviews, Rankings and more by FictionMe',
  ogDescription: 'Dive into FictionMe for captivating novel reviews, up-to-date rankings, and essential writing resources. Discover your next favorite story today!',
  twitterTitle: 'Novels, Books and Stories reviews, Rankings and more by FictionMe',
  twitterDescription: 'Dive into FictionMe for captivating novel reviews, up-to-date rankings, and essential writing resources. Discover your next favorite story today!',
});
</script>

<template>
  <div class="container py-8">
    <div class="page-content flex flex-col gap-8 max-md:gap-6">
      <reviews-cta-banner v-if="currentPage < 2" />

      <div class="flex flex-col gap-4 max-md:gap-2">
        <h1 class="text-3xl font-bold max-md:text-2xl">Recent Novel Reviews</h1>

        <div v-loading="pending" class="mb-8 flex flex-col gap-4 max-md:gap-2">
          <nuxt-link
              :to="`/book-reviews/${review.slug}`"
              v-for="review in data?.data"
              :title="review.title"
              class="group review-item w-full grid grid-cols-[auto_1fr] gap-5 max-md:gap-2 bg-white rounded-xl dark:border-zinc-700 dark:bg-zinc-800"
          >
            <div class="relative overflow-hidden w-[289px] max-md:w-[132px] h-[243px] max-md:h-[138px] rounded-xl bg-gray-200 dark:bg-zinc-600">
              <nuxt-img
                  :provider="config.public.imageProvider"
                  :src="config.public.imageProvider === 'ipx' ? review.image_url : review.image_path"
                  loading="lazy"
                  :alt="review.title"
                  :quality="80"
                  :height="isMobile ? 138 : 243"
                  format="webp"
                  class="w-full h-full object-cover transition-transform duration-1000 transform group-hover:scale-110"
              />
            </div>
            <div class="flex flex-col gap-4 max-md:gap-2 p-4 max-md:p-2 pl-0">
              <p v-if="review.genres && review.genres.length > 0" class="text-primary uppercase max-md:text-xs">{{ review.genres[0].name }}</p>
              <h3 class="text-2xl leading-6 font-bold text-gray-950 dark:text-gray-200 max-md:text-base max-md:line-clamp-3 max-xl:line-clamp-2 max-md:leading-5">{{ review.title }}</h3>
              <p class="text-base flex-grow line-clamp-3 text-gray-600 dark:text-gray-400 max-md:hidden max-xl:line-clamp-2">{{ review.description }}</p>
              <div class="flex max-md:justify-end max-md:flex-grow">
                <span class="w-max p-2 border border-violet-800 font-semibold flex items-center justify-between rounded text-violet-800 group-hover:border-primary group-hover:text-primary dark:border-primary dark:text-primary max-md:p-0 max-md:border-0 ">
                <span>Read Now</span>
                <icon name="fm:arrow-right" class="ml-2 text-2xl" />
              </span>
              </div>
            </div>
          </nuxt-link>
        </div>

        <pagination
            v-if="data?.meta && data.meta.last_page > 1"
            v-model="currentPage"
            :total-pages="data.meta.last_page"
            @changed="onChangePage"
        />
      </div>

      <lazy-f-card
          v-if="popularBooks && popularBooks.length > 0"
          title="Popular Novels You May Like"
      >
        <div class="flex gap-5 max-md:gap-3 overflow-auto scrollbar-hidden">
          <book-card
              v-for="book in popularBooks"
              :book="book"
              :key="book.id"
          />
        </div>
      </lazy-f-card>

      <cta-banner />
    </div>
  </div>
</template>

<style scoped>
.review-item {
  box-shadow: 0px 12px 80px 0px rgba(34, 37, 49, 0.04), 0px 1px 10px 0px rgba(34, 37, 49, 0.04);

  &:hover {
    box-shadow: 0px 12px 80px 0px rgba(34, 37, 49, 0.04), 0px 1px 10px 0px rgba(48, 16, 68, 0.14);
  }
}
</style>